import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Users, CheckCircle2, Mail, Building2 } from 'lucide-react';

const FOREST = '#2C4A3B';
const FOREST_DARK = '#1E3329';
const TERRA = '#C47A5E';
const CREAM = '#F9F6F0';
const CHARCOAL = '#1C1917';

const TEAM_SIZES = [
  '1–10 clinicians',
  '11–25 clinicians',
  '26–50 clinicians',
  '51–100 clinicians',
  '100–250 clinicians',
  '250+ clinicians',
];

export default function ContactSalesPage() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    organization: '',
    team_size: TEAM_SIZES[3],
    message: '',
  });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/contact/sales`, form);
      setSubmitted(true);
      toast.success('Thanks — our team will reach out within 1 business day.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ fontFamily: "'Manrope', sans-serif", background: CREAM }}
      data-testid="contact-sales-page"
    >
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-[0.08]" style={{ background: FOREST }} aria-hidden="true" />
      <div className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full opacity-[0.1]" style={{ background: TERRA }} aria-hidden="true" />

      <div className="w-full max-w-xl relative">
        <button
          type="button"
          onClick={() => navigate('/landing')}
          className="mb-5 inline-flex items-center gap-2 text-sm text-stone-600 hover:text-stone-900 transition-colors"
          data-testid="contact-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </button>

        <div
          className="rounded-3xl shadow-xl p-8 sm:p-10 border"
          style={{ background: '#fff', borderColor: '#E3E8E4' }}
        >
          {submitted ? (
            <div className="text-center py-6" data-testid="contact-success">
              <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ background: '#E3E8E4' }}>
                <CheckCircle2 className="w-8 h-8" style={{ color: FOREST }} />
              </div>
              <h1 className="text-2xl font-bold mb-2" style={{ color: CHARCOAL }}>
                We'll be in touch shortly.
              </h1>
              <p className="text-sm mb-6" style={{ color: '#6B8E7B' }}>
                Your Plexura AI Scribe enterprise team will reach out within 1 business day to schedule a call and walk you through pricing, provisioning, and onboarding.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => navigate('/landing')}
                  className="h-11 rounded-xl text-white font-semibold"
                  style={{ background: FOREST }}
                  data-testid="contact-done-btn"
                >
                  Back to home
                </Button>
                <Button
                  onClick={() => setSubmitted(false)}
                  variant="outline"
                  className="h-11 rounded-xl"
                  data-testid="contact-another-btn"
                >
                  Submit another request
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: FOREST }}>
                  <Users className="w-5 h-5" style={{ color: CREAM }} />
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: TERRA }}>Enterprise · Plexura</p>
                  <h1 className="text-2xl font-bold tracking-tight" style={{ color: CHARCOAL }}>Talk to Sales</h1>
                </div>
              </div>
              <p className="text-sm mb-6" style={{ color: '#6B8E7B' }}>
                For practices with more than 50 clinicians. Tell us about your team — we'll put together custom pricing, SSO provisioning, and a dedicated onboarding plan.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium" style={{ color: CHARCOAL }}>Your Name</Label>
                    <Input id="name" name="name" required value={form.name} onChange={handleChange}
                      placeholder="Dr. Jordan Reese"
                      data-testid="contact-name-input"
                      className="mt-1.5 h-11 rounded-xl" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm font-medium" style={{ color: CHARCOAL }}>Work Email</Label>
                    <Input id="email" name="email" type="email" required value={form.email} onChange={handleChange}
                      placeholder="jordan@clinic.com"
                      data-testid="contact-email-input"
                      className="mt-1.5 h-11 rounded-xl" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="organization" className="text-sm font-medium" style={{ color: CHARCOAL }}>Practice / Organization</Label>
                  <Input id="organization" name="organization" required value={form.organization} onChange={handleChange}
                    placeholder="Evergreen Behavioral Health"
                    data-testid="contact-org-input"
                    className="mt-1.5 h-11 rounded-xl" />
                </div>

                <div>
                  <Label htmlFor="team_size" className="text-sm font-medium" style={{ color: CHARCOAL }}>Team size</Label>
                  <select
                    id="team_size"
                    name="team_size"
                    value={form.team_size}
                    onChange={handleChange}
                    data-testid="contact-team-size-select"
                    className="mt-1.5 w-full h-11 rounded-xl border px-3 text-sm bg-white focus:outline-none focus:ring-2"
                    style={{ borderColor: '#E3E8E4' }}
                  >
                    {TEAM_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div>
                  <Label htmlFor="message" className="text-sm font-medium" style={{ color: CHARCOAL }}>What would you like to discuss? <span className="text-stone-400 font-normal">(optional)</span></Label>
                  <textarea
                    id="message"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={4}
                    data-testid="contact-message-input"
                    placeholder="EHR integrations, SSO, compliance reviews, timeline…"
                    className="mt-1.5 w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2"
                    style={{ borderColor: '#E3E8E4' }}
                  />
                </div>

                <Button type="submit" disabled={saving}
                  className="w-full h-11 rounded-xl text-white font-semibold shadow-md hover:shadow-lg transition-all"
                  style={{ background: saving ? FOREST_DARK : FOREST }}
                  data-testid="contact-submit-btn">
                  {saving ? 'Sending…' : 'Request a call with Sales'}
                </Button>

                <div className="pt-3 border-t border-stone-100 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-stone-500">
                  <span className="inline-flex items-center gap-1.5"><Mail className="w-3 h-3" /> Response within 1 business day</span>
                  <span className="inline-flex items-center gap-1.5"><Building2 className="w-3 h-3" /> BAA & custom DPA available</span>
                </div>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-xs mt-6" style={{ color: '#6B8E7B' }}>
          Prefer solo pricing? <Link to="/register" className="font-semibold hover:underline" style={{ color: TERRA }}>Start a 7-day free trial</Link>
        </p>
      </div>
    </div>
  );
}
